import moment from 'moment-timezone';
import type AvailableTime from '../model/AvailableTime';

const DATE_FORMAT = 'YYYY-MM-DD';
const TIME_FORMAT = 'HH:mm';
export const DEFAULT_APPOINTMENT_LENGTH_MINUTES = 45;

interface RawTimeslot {
  start: string
  end: string
}

export interface ExpandedTimeslot {
  start: string
  end: string
}

export const parseAppointmentLengthMinutes = (appointmentLength?: string): number => {
  const raw = appointmentLength?.split('-')[0];
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_APPOINTMENT_LENGTH_MINUTES;
};

export const expandTimeslotsForDay = (
  date: string,
  rawSlots: RawTimeslot[],
  appointmentLengthMinutes: number,
): ExpandedTimeslot[] => {
  const slots: ExpandedTimeslot[] = [];
  rawSlots.forEach(({ start, end }) => {
    let slotStart = moment(`${date} ${start}`, `${DATE_FORMAT} ${TIME_FORMAT}`);
    const slotEnd = moment(`${date} ${end}`, `${DATE_FORMAT} ${TIME_FORMAT}`);
    // Overnight window (e.g. 11:00 -> 00:05): end falls on the next day.
    if (slotEnd.isSameOrBefore(slotStart)) {
      slotEnd.add(1, 'day');
    }
    while (slotStart.clone().add(appointmentLengthMinutes, 'minutes').isSameOrBefore(slotEnd)) {
      const next = slotStart.clone().add(appointmentLengthMinutes, 'minutes');
      slots.push({ start: slotStart.format(TIME_FORMAT), end: next.format(TIME_FORMAT) });
      slotStart = next;
    }
  });
  return slots;
};

export const dayHasFutureSlot = (
  day: AvailableTime,
  appointmentLengthMinutes: number = DEFAULT_APPOINTMENT_LENGTH_MINUTES,
): boolean => {
  const now = moment();
  return expandTimeslotsForDay(day.date, day.availableTimeslots, appointmentLengthMinutes)
    .some((slot) => moment(`${day.date} ${slot.start}`, `${DATE_FORMAT} ${TIME_FORMAT}`).isAfter(now));
};

export const getEarliestFutureSlotMoment = (
  days: AvailableTime[],
  appointmentLengthMinutes: number = DEFAULT_APPOINTMENT_LENGTH_MINUTES,
): moment.Moment | null => {
  const now = moment();
  const earliest = days
    .flatMap((day) => expandTimeslotsForDay(day.date, day.availableTimeslots, appointmentLengthMinutes)
      .map((slot) => moment(`${day.date} ${slot.start}`, `${DATE_FORMAT} ${TIME_FORMAT}`)))
    .filter((slotMoment) => slotMoment.isAfter(now))
    .sort((a, b) => a.valueOf() - b.valueOf())[0];
  return earliest ?? null;
};
